import React, { useState, useRef, useEffect } from 'react';
import './HTMLEditor.scss';
import CodeEditor, { CodeEditorProps } from '@amzn/awsui-components-console/code-editor';
import Modal from '@amzn/awsui-components-console/modal';
import Box from '@amzn/awsui-components-console/box';
import SpaceBetween from '@amzn/awsui-components-console/space-between';
import Button from '@amzn/awsui-components-console/button';
import 'ace-builds/src-noconflict/ace.js';
import 'ace-builds/css/ace.css';
import 'ace-builds/css/theme/dawn.css';
import { aceLoader } from './utils/aceLoader';
import type ace from 'ace-builds';
import { readHTMLFile } from './utils/fileUtils';
import { useDragDrop } from './hooks/useDragDrop';
import { useContentEditing } from './hooks/useContentEditing';
import { useLinkEditing } from './hooks/useLinkEditing';
import { useToolbarActions } from './hooks/useToolbarActions';
import Toolbar from './components/Toolbar';
import LinkEditPopover from './components/LinkEditPopover';
import TextContent from '@amzn/awsui-components-console/text-content';

interface HTMLEditorProps {
  initialContent?: string;
  isEditMode?: boolean;
  onContentChange?: (content: string) => void;
  saveStatus?: 'idle' | 'saving' | 'success' | 'error' | 'edited';
}

const HTMLEditor: React.FC<HTMLEditorProps> = ({
  initialContent = '',
  isEditMode: controlledEditMode,
  onContentChange,
  saveStatus = 'idle',
}) => {
  const [htmlContent, setHtmlContent] = useState<string>(initialContent);
  const [isEditMode, setIsEditMode] = useState<boolean>(controlledEditMode ?? true);
  const [isSourceView, setIsSourceView] = useState<boolean>(false);
  const [sourceCode, setSourceCode] = useState<string>(initialContent);
  const [hasSourceChanges, setHasSourceChanges] = useState<boolean>(false);
  const [codeEditorPreferences, setCodeEditorPreferences] = useState<CodeEditorProps.Preferences | undefined>({
    wrapLines: true,
    theme: 'dawn',
  });
  const [showUnsavedModal, setShowUnsavedModal] = useState<boolean>(false);
  const [aceEditor, setAceEditor] = useState<typeof ace | undefined>(undefined);
  const [aceLoading, setAceLoading] = useState<boolean>(true);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const contentRef = useRef<HTMLDivElement>(null);
  const editorContainerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (controlledEditMode !== undefined) {
      setIsEditMode(controlledEditMode);
    }
  }, [controlledEditMode]);

  useEffect(() => {
    setHtmlContent(initialContent);
    setSourceCode(initialContent);
  }, [initialContent]);

  // Load ace editor
  useEffect(() => {
    aceLoader()
      .then((ace) => {
        setAceEditor(ace);
      })
      .finally(() => {
        setAceLoading(false);
      });
  }, []);

  const handleFileUpload = async (file: File) => {
    try {
      const content = await readHTMLFile(file);
      setHtmlContent(content);
      if (onContentChange) {
        onContentChange(content);
      }
    } catch (error) {
      alert(error instanceof Error ? error.message : 'Failed to upload file');
    }
  };

  const { isDragging, handleDrop, handleDragOver, handleDragLeave } = useDragDrop({
    onFileDrop: handleFileUpload,
  });

  const handleContentChangeInternal = (content: string) => {
    setHtmlContent(content);
    setSourceCode(content);
    if (onContentChange) {
      console.log('handle content change');
      onContentChange(content);
    }
  };

  const handleSourceCodeChange = (value: string) => {
    setSourceCode(value);
    setHasSourceChanges(value !== htmlContent);
  };

  const handleSaveSource = () => {
    setHtmlContent(sourceCode);
    setHasSourceChanges(false);
    if (onContentChange) {
      onContentChange(sourceCode);
    }
  };

  const handleToggleSourceView = () => {
    if (isSourceView && hasSourceChanges) {
      setShowUnsavedModal(true);
    } else {
      setIsSourceView(!isSourceView);
    }
  };

  const handleUnsavedModalSave = () => {
    handleSaveSource();
    setShowUnsavedModal(false);
    setIsSourceView(false);
  };

  const handleUnsavedModalDiscard = () => {
    setSourceCode(htmlContent);
    setHasSourceChanges(false);
    setShowUnsavedModal(false);
    setIsSourceView(false);
  };

  const handleUnsavedModalCancel = () => {
    setShowUnsavedModal(false);
  };

  const {
    selectedElement,
    handleContentMouseDown,
    handleContentMouseOver,
    handleContentMouseOut,
    handleContentBlur,
    handleContentKeyDown,
  } = useContentEditing({
    isEditMode,
    contentRef,
    onContentChange: handleContentChangeInternal,
  });

  const { editingAnchor, setEditingAnchor, handleLinkClick, handleLinkSave, handleLinkEditClose } = useLinkEditing({
    isEditMode,
    contentRef,
    onContentChange: handleContentChangeInternal,
  });

  const { applyFormatting } = useToolbarActions({
    selectedElement,
    contentRef,
    onContentChange: handleContentChangeInternal,
    setEditingAnchor,
  });

  const handleFileInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      handleFileUpload(file);
    }
  };

  return (
    <div className="html-editor" ref={editorContainerRef}>
      {!htmlContent ? (
        <div
          className={`upload-area ${isDragging ? 'dragging' : ''}`}
          onDrop={handleDrop}
          onDragOver={handleDragOver}
          onDragLeave={handleDragLeave}
        >
          <div className="upload-content">
            <div className="upload-text">
              <TextContent>
                <h3>Upload HTML File</h3>
                <p>Drag and drop an HTML file here, or click to browse</p>
              </TextContent>
            </div>

            <input ref={fileInputRef} type="file" accept=".html,.htm" onChange={handleFileInputChange} style={{ display: 'none' }} />
            <Button variant="normal" iconName="upload" onClick={() => fileInputRef.current?.click()}>
              Browse Files
            </Button>
          </div>
        </div>
      ) : (
        <div className={`content-area ${isEditMode ? 'edit-mode' : 'view-mode'}`}>
          {isEditMode && (
            <>
              <Toolbar
                isEditMode={isEditMode}
                hasSelection={!!selectedElement}
                onFormat={applyFormatting}
                isSourceView={isSourceView}
                hasSourceChanges={hasSourceChanges}
                onToggleSourceView={handleToggleSourceView}
                onSaveSource={handleSaveSource}
                saveStatus={saveStatus}
              />
            </>
          )}
          {isSourceView ? (
            <div className="source-editor-container">
              <CodeEditor
                ace={aceEditor}
                language="html"
                value={sourceCode}
                onDelayedChange={(e) => handleSourceCodeChange(e.detail.value)}
                preferences={codeEditorPreferences}
                onPreferencesChange={(e) => setCodeEditorPreferences(e.detail)}
                loading={aceLoading}
                editorContentHeight={window.innerHeight - 80}
                themes={{
                  light: ['dawn'],
                  dark: ['dawn'],
                }}
                i18nStrings={{
                  loadingState: 'Loading code editor',
                  errorState: 'There was an error loading the code editor',
                  errorStateRecovery: 'Retry',
                  editorGroupAriaLabel: 'Code editor',
                  statusBarGroupAriaLabel: 'Status bar',
                  cursorPosition: (row: number, column: number) => `Ln ${row}, Col ${column}`,
                  errorsTab: 'Errors',
                  warningsTab: 'Warnings',
                  preferencesButtonAriaLabel: 'Preferences',
                  paneCloseButtonAriaLabel: 'Close',
                  preferencesModalHeader: 'Preferences',
                  preferencesModalWrapLines: 'Wrap lines',
                  preferencesModalTheme: 'Theme',
                  preferencesModalLightThemes: 'Light themes',
                  preferencesModalDarkThemes: 'Dark themes',
                  preferencesModalConfirm: 'Confirm',
                  preferencesModalCancel: 'Cancel',
                }}
              />
            </div>
          ) : (
            <>
              <div
                ref={contentRef}
                className="html-content"
                dangerouslySetInnerHTML={{ __html: htmlContent }}
                onMouseDown={handleContentMouseDown}
                onClick={handleLinkClick}
                onBlur={handleContentBlur}
                onKeyDown={handleContentKeyDown}
                onMouseOver={handleContentMouseOver}
                onMouseOut={handleContentMouseOut}
              />
              {editingAnchor && <LinkEditPopover anchorElement={editingAnchor} onSave={handleLinkSave} onClose={handleLinkEditClose} />}
            </>
          )}
        </div>
      )}

      <Modal
        onDismiss={handleUnsavedModalCancel}
        visible={showUnsavedModal}
        footer={
          <Box float="right">
            <SpaceBetween direction="horizontal" size="xs">
              <Button variant="link" onClick={handleUnsavedModalCancel}>
                Cancel
              </Button>
              <Button variant="normal" onClick={handleUnsavedModalDiscard}>
                Discard Changes
              </Button>
              <Button variant="primary" onClick={handleUnsavedModalSave}>
                Save Changes
              </Button>
            </SpaceBetween>
          </Box>
        }
        header="Unsaved Changes"
      >
        You have unsaved changes in the source code editor. Would you like to save them before switching back to the visual editor?
      </Modal>
    </div>
  );
};

export default HTMLEditor;
